import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { Icon, divIcon } from 'leaflet';
import styled from 'styled-components';
import { colors, spacing, typography, borderRadius } from '../styles/theme';
import { Event } from '../types/api';
import config from '../config';
import { getEventLocalSortKey } from '../utils/eventDateTime';
import { buildPinHtml, getMarkerOpacity, getVibeStyle } from '../utils/eventMapStyles';

/** Map marker coordinates (Leaflet uses lat/lng tuples). */
export interface Coordinates {
  lat: number;
  lng: number;
}

/** Pin precision: 'venue' = street-level, 'location' = city-centroid. */
export type CoordinateSource = 'venue' | 'location';

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: config.leafletIcons.iconRetinaUrl,
  iconUrl: config.leafletIcons.iconUrl,
  shadowUrl: config.leafletIcons.shadowUrl,
});

const MapWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'height',
})<{ height: string }>`
  width: 100%;
  height: ${(props) => props.height};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  border: 1px solid ${colors.border.light};
  
  .leaflet-container {
    height: 100%;
    width: 100%;
  }
`;

const PopupContent = styled.div`
  padding: ${spacing[2]};
  min-width: 250px;
`;

const PopupTitle = styled.h3`
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
  line-height: ${typography.lineHeights.tight};
`;

export interface EventMarkerData extends Omit<Event, 'coordinates'> {
  coordinates: Coordinates;
  /** Optional pin-precision hint; rendered in popups as a small label. */
  coordinatesSource?: CoordinateSource;
}

/**
 * Marker data carries Leaflet's `{lat, lng}`, while the shared presentation
 * helpers expect the API's `{latitude, longitude, source}`. Convert rather than
 * cast so the helpers keep their real types.
 */
export function toEventShape(marker: EventMarkerData): Event {
  return {
    ...marker,
    coordinates: {
      latitude: marker.coordinates.lat,
      longitude: marker.coordinates.lng,
      source: marker.coordinatesSource ?? 'venue',
    },
  };
}

interface EventMapProps {
  events: EventMarkerData[];
  center?: Coordinates;
  zoom?: number;
  /** Optional “you are here” marker. */
  userLocation?: Coordinates;
  /** Fired (debounced via Leaflet `moveend`) whenever the user pans/zooms. */
  onMapMove?: (center: Coordinates, zoom: number) => void;
  /** Event hovered in either the map or companion list — its pin is enlarged. */
  highlightedEventId?: number | null;
  /** Event hovered/focused in the companion list — the map pans to its pin. */
  focusedEventId?: number | null;
  /** Fired when a marker is hovered, so a companion list can highlight its row. */
  onMarkerHover?: (eventId: number | null) => void;
  /** Fired when a marker is selected; the parent owns the detail surface. */
  onMarkerSelect?: (events: EventMarkerData[]) => void;
  /** CSS height for the map. Pass '100%' to fill a sized container. */
  height?: string;
}

/** Internal: subscribe to Leaflet move events and bubble them up. */
const MapMoveTracker: React.FC<{ onMove: (c: Coordinates, z: number) => void }> = ({ onMove }) => {
  useMapEvents({
    moveend: (e) => {
      const map = e.target;
      const c = map.getCenter();
      onMove({ lat: c.lat, lng: c.lng }, map.getZoom());
    },
  });
  return null;
};

/** Internal: re-center the Leaflet map when the `center`/`zoom` props change. */
const MapViewController: React.FC<{ center: Coordinates; zoom: number }> = ({ center, zoom }) => {
  const map = useMapEvents({});
  React.useEffect(() => {
    map.setView([center.lat, center.lng], zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center.lat, center.lng, zoom]);
  return null;
};

// Custom marker icon for events. Colour encodes the event's vibe family and
// opacity encodes how far out it is, so a glance at the map shows both what
// kind of event it is and how soon it happens.
const createEventIcon = (events: EventMarkerData[], isHighlighted = false) => {
  const primary = events[0];
  const style = getVibeStyle(toEventShape(primary));
  const opacity = Math.max(...events.map((e) => getMarkerOpacity(toEventShape(e))));
  const size = isHighlighted ? 40 : 32;

  return divIcon({
    className: 'event-pin',
    html: buildPinHtml({
      color: style.color,
      opacity,
      isHighlighted,
      count: events.length,
    }),
    iconSize: [size, size * 1.25],
    iconAnchor: [size / 2, size * 1.25],
    popupAnchor: [0, -size * 1.1],
  });
};

/** Pan to and enlarge the pin for the event hovered in the companion list. */
const HighlightController: React.FC<{
  focusedEventId: number | null;
  positions: Map<number, Coordinates>;
}> = ({ focusedEventId, positions }) => {
  const map = useMap();

  React.useEffect(() => {
    if (focusedEventId == null) return;
    const position = positions.get(focusedEventId);
    if (!position) return;
    map.panTo([position.lat, position.lng], { animate: true, duration: 0.4 });
  }, [focusedEventId, positions, map]);

  return null;
};

// "You are here" marker — a small accent dot rendered via divIcon so we don't
// need extra image assets.
const userIcon = divIcon({
  className: 'user-location-icon',
  html: `<div style="
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${colors.primary[600]};
    border: 3px solid white;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.25);
  "></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const EventMap: React.FC<EventMapProps> = ({ 
  events, 
  center = { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
  zoom = 4,
  userLocation,
  onMapMove,
  highlightedEventId = null,
  focusedEventId = null,
  onMarkerHover,
  onMarkerSelect,
  height = '600px',
}) => {
  // Group events by location for clustering, then sort each group by date for
  // the parent-owned detail pane.
  const groupedEvents = events.reduce((acc, event) => {
    const key = `${event.coordinates.lat.toFixed(4)},${event.coordinates.lng.toFixed(4)}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(event);
    return acc;
  }, {} as Record<string, EventMarkerData[]>);

  const startTime = (e: EventMarkerData) => {
    return getEventLocalSortKey(e.startDate) ?? Number.POSITIVE_INFINITY;
  };
  for (const key of Object.keys(groupedEvents)) {
    groupedEvents[key].sort((a, b) => startTime(a) - startTime(b));
  }

  // Every event's position, so the companion list can centre any pin.
  const positions = React.useMemo(() => {
    const map = new Map<number, Coordinates>();
    for (const event of events) map.set(event.eventId, event.coordinates);
    return map;
  }, [events]);

  return (
    <MapWrapper height={height}>
      <MapContainer 
        center={[center.lat, center.lng]} 
        zoom={zoom} 
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution={config.externalServices.leafletTileAttribution}
          url={config.externalServices.leafletTileUrl}
        />

        {onMapMove && <MapMoveTracker onMove={onMapMove} />}
        <MapViewController center={center} zoom={zoom} />
        <HighlightController focusedEventId={focusedEventId} positions={positions} />

        {userLocation && (
          <Marker
            position={[userLocation.lat, userLocation.lng]}
            icon={userIcon}
          >
            <Popup maxWidth={200}>
              <PopupContent>
                <PopupTitle>You are here</PopupTitle>
              </PopupContent>
            </Popup>
          </Marker>
        )}
        
        {Object.entries(groupedEvents).map(([key, eventsAtLocation]) => {
          const firstEvent = eventsAtLocation[0];
          const isHighlighted = highlightedEventId != null
            && eventsAtLocation.some((e) => e.eventId === highlightedEventId);

          return (
            <Marker
              key={key}
              position={[firstEvent.coordinates.lat, firstEvent.coordinates.lng]}
              icon={createEventIcon(eventsAtLocation, isHighlighted)}
              zIndexOffset={isHighlighted ? 1000 : 0}
              eventHandlers={{
                mouseover: () => onMarkerHover?.(firstEvent.eventId),
                mouseout: () => onMarkerHover?.(null),
                click: () => onMarkerSelect?.(eventsAtLocation),
              }}
            />
          );
        })}
      </MapContainer>
    </MapWrapper>
  );
};

export default EventMap;
